/**
 * Validates a post-login redirect target captured from react-router location
 * state, so it can only ever be a same-origin relative path — never an
 * absolute URL or protocol-relative path (open-redirect guard).
 */
export function getSafeRedirectPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate) return null;
  // A string starting with a single '/' can never itself be an absolute URL —
  // no scheme can precede a leading '/' — so no separate '://' check is needed,
  // and that check was actively wrong: it rejected legitimate same-origin paths
  // whose *query string* happens to contain '://' (e.g. /page?next=https://x).
  // '//' and '/\' both risk browser URL-normalization turning this into a
  // protocol-relative reference to a different origin (some browsers normalize
  // '\' to '/', so '/\evil.com' becomes '//evil.com').
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) {
    return null;
  }
  return candidate;
}

/** Reconstructs a "pathname + search" string from a captured router Location-like object. */
export function getSafeRedirectFromLocationState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const from = (state as { from?: { pathname?: unknown; search?: unknown } }).from;
  if (!from || typeof from !== 'object') return null;
  const pathname = typeof from.pathname === 'string' ? from.pathname : '';
  const search = typeof from.search === 'string' ? from.search : '';
  return getSafeRedirectPath(`${pathname}${search}`);
}
